import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import moment from 'moment'
import enDictionary from 'assets/dictionaries/en.json'
import ruDictionary from 'assets/dictionaries/ru.json'
import zhDictionary from 'assets/dictionaries/zh.json'
import LanguageDetector from "i18next-browser-languagedetector";
import { registerUiKitI18n } from '@vedmarket/ui-kit';
import {getIpInfo, IpInfo} from "../utils/ip";
import 'moment/locale/ru'
import 'moment/locale/zh-cn'

const enum LOCALES {
  ENGLISH = 'en',
  RUSSIAN = 'ru',
  CHINESE = 'zh'
}

const resources = {
  [LOCALES.ENGLISH]: { translation: enDictionary },
  [LOCALES.RUSSIAN]: { translation: ruDictionary },
  [LOCALES.CHINESE]: { translation: zhDictionary }
}

const LANGUAGE_STORAGE_KEY = 'i18nextLng';

export const supportedLngs = {
  ru: "Русский",
  en: "English",
  zh: "中文",
};

export const getLanguageCode = (lang?: string | null) => {
  const language = lang?.toLowerCase();

  if (language?.startsWith(LOCALES.ENGLISH)) return LOCALES.ENGLISH;
  if (language?.startsWith(LOCALES.RUSSIAN)) return LOCALES.RUSSIAN;
  if (language?.startsWith(LOCALES.CHINESE)) return LOCALES.CHINESE;

  return LOCALES.RUSSIAN;
}

export const getStoredAppLanguage = () => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch (error) {
    return null;
  }
}

export const changeAppLanguage = (lang?: string | null) => {
  const language = getLanguageCode(lang);

  i18n.changeLanguage(language);
  moment.locale(language === LOCALES.CHINESE ? 'zh-cn' : language);

  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.error(error);
  }

  return language;
}

export const getAcceptLanguage = (lang?: string | null) => {
  const language = getLanguageCode(lang);

  if (language === LOCALES.ENGLISH) return 'en-US';
  if (language === LOCALES.CHINESE) return 'zh-CN';

  return 'ru-RU';
}

const isRussianLanguage = (language?: string) => {
  return language?.toLowerCase().startsWith(LOCALES.RUSSIAN);
}

const isChineseLanguage = (language?: string) => {
  const currentLanguage = language?.toLowerCase();

  return Boolean(currentLanguage?.startsWith(LOCALES.CHINESE) || currentLanguage?.includes('chinese'));
}

const getLanguageByCountry = (countryCode?: string) => {
  const country = countryCode?.toUpperCase();

  if (country === 'RU') return LOCALES.RUSSIAN;
  if ([ 'CN', 'HK', 'MO', 'TW' ].includes(country || '')) return LOCALES.CHINESE;

  return LOCALES.ENGLISH;
}

const getLanguageFromIpInfo = ({ country, country_code, languages }: IpInfo) => {
  const language = languages?.split(',').map((item) => item.trim()).find((item) => {
    return isRussianLanguage(item) || isChineseLanguage(item);
  });

  if (isRussianLanguage(language)) return LOCALES.RUSSIAN;

  if (isChineseLanguage(language)) return LOCALES.CHINESE;

  return getLanguageByCountry(country_code || country);
}

export const getLanguageByIp = async () => {
  const data = await getIpInfo();

  if (data.country || data.country_code || data.languages) {
    return getLanguageFromIpInfo(data);
  }

  return LOCALES.ENGLISH;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: Object.keys(supportedLngs),
    fallbackLng: LOCALES.RUSSIAN,
    interpolation: {
      escapeValue: false,
    },
  })

registerUiKitI18n(i18n);

export default i18n
